import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import {
    AdvancedSearchGroup,
    ApiQuery,
    ApiQueryBuilder,
    EmptyStructuredQuery,
    LogicalOperand,
    Operand,
    Restriction,
    SortDirection,
    SortInfo,
    StructuredQuery
} from '@stylehaven/components';
import { ApiFragment } from '../entities/api-fragment.entity';
import { MetadataField } from '../entities/metadata-field.entity';
import { RecordType } from '../entities/metadata.entity';
import { PaginatedResults } from '../entities/pagination/paginated-results.entity';
import { RecordSearch } from '../entities/record-search.entity';
import { RecordSandboxService } from '../sandbox/record-sandbox.service';

export type ParentKind = 'dossier' | 'stuk';

/** What the caller knows about the expansion it is (re)filling. */
export interface ChildrenRequest {
    parentKind?: ParentKind;
    /** Where the next capped batch starts; 0 for the first one. */
    startIndex?: number;
    /** The overview's own sort, inherited only where the field exists at stuk level. */
    sort?: SortInfo | null;
    /**
     * What the tree runs under: the page's own restrictions, unless the page scopes its top level to something
     * no stuk can be — a `RecordType: Dossier` pin — and hands the container a security-only one instead.
     */
    restrictions?: Restriction;
}

/**
 * HFBD-7a: the tree's data access. The hierarchical table is presentational and fetches nothing; this is
 * what issues the requests behind it. It invents no new seam — everything goes through the unchanged
 * {@link RecordSandboxService} — and can be unit-tested without constructing the overview container.
 */
@Injectable()
export class HierarchyService {
    /** One expansion loads this many children, then offers a "Toon meer stukken" row for the rest. */
    static readonly CHILDREN_BATCH_SIZE: number = 25;

    static readonly ANY_VALUE: string = '*';

    static readonly MAX_CHILD_LEVEL: number = 2;

    /** Where children fall back to when the overview sorts on a field a stuk does not have. */
    static readonly DEFAULT_CHILD_SORT: SortInfo = { field: MetadataField.Title, direction: SortDirection.ASC };

    /** The overview's sort is inherited only for fields that exist at stuk level as well — see the spec's §9. */
    private static readonly INHERITABLE_SORT_FIELDS: string[] = [
        MetadataField.Title,
        MetadataField.UserLastModifiedDate
    ];

    constructor(private readonly sandbox: RecordSandboxService) {}

    /** One request per expanded dossier: the per-dossier cap makes batching across dossiers useless here. */
    childrenFor(parentId: string, request: ChildrenRequest = {}): Observable<PaginatedResults<ApiFragment>> {
        const sort: SortInfo = HierarchyService.toChildSort(request.sort);

        const search: RecordSearch = {
            // The shared builder rather than `LuceneHelper`: this query needs no faked field, and only the
            // shared one makes the restrictions a *required* clause instead of an optional one.
            Q: ApiQueryBuilder.queryToLuceneQuery(
                this.childrenQuery(parentId, request.parentKind ?? 'dossier', request.restrictions)
            ),
            StartIndex: request.startIndex ?? 0,
            NrOfResults: HierarchyService.CHILDREN_BATCH_SIZE,
            Sort: sort.field,
            Direction: sort.direction
        };

        return this.sandbox.search(search);
    }

    static toChildSort(sort: SortInfo | null | undefined): SortInfo {
        return sort && HierarchyService.INHERITABLE_SORT_FIELDS.includes(sort.field)
            ? sort
            : HierarchyService.DEFAULT_CHILD_SORT;
    }

    /**
     * A dossier's stukken are its attached children *and* its candidate children — the second arm is what
     * today's documents overview shows too, so the tree would lose rows without it.
     *
     * The two arms are not symmetrical, which is why this does not reuse the
     * `ChildrenAndCandidateChildren` fake field: that gives both fields the same value. `ReferenceCodesDossier`
     * holds a full-length record id and is matched exactly, while `RelationsIsCandidateChildOf` holds a
     * *fragment* id and needs the trailing wildcard — the same pair the backend's own children lookup uses.
     */
    private childrenQuery(parentId: string, parentKind: ParentKind, restrictions?: Restriction): ApiQuery {
        const typeTerms: AdvancedSearchGroup['Terms'] = [
            { DottedKey: MetadataField.RecordType, Operand: Operand.Include, Values: [RecordType.Document] }
        ];

        if (parentKind === 'dossier') {
            typeTerms.push({
                DottedKey: MetadataField.ReferenceCodesDocument,
                Operand: Operand.Exclude,
                Values: [HierarchyService.ANY_VALUE]
            });
        }

        const typeGroup: AdvancedSearchGroup = { LogicalOperand: LogicalOperand.And, Terms: typeTerms };

        const parentGroup: AdvancedSearchGroup =
            parentKind === 'stuk'
                ? {
                      LogicalOperand: LogicalOperand.Or,
                      Terms: [
                          {
                              DottedKey: MetadataField.ParentRecordId,
                              Operand: Operand.Include,
                              Values: [`${parentId}*`]
                          }
                      ]
                  }
                : {
                      LogicalOperand: LogicalOperand.Or,
                      Terms: [
                          {
                              DottedKey: MetadataField.ReferenceCodesDossier,
                              Operand: Operand.Include,
                              Values: [parentId]
                          },
                          {
                              DottedKey: MetadataField.RelationsIsCandidateChildOf,
                              Operand: Operand.Include,
                              Values: [`${parentId}*`]
                          }
                      ]
                  };

        const structuredQuery: StructuredQuery = {
            ...EmptyStructuredQuery,
            AdvancedSearch: { LogicalOperand: LogicalOperand.And, Groups: [typeGroup, parentGroup] }
        };

        return new ApiQuery(structuredQuery, restrictions);
    }
}
